import { Space } from "antd";
import { memo } from "react";
import AdyenDocumentPanel from "./AdyenDocumentPanel";
import {
  controlOwner,
  groupRowsBySubmission,
  otherSubmissionReasons,
  rowsForPerson,
  submissionDisplayRow,
} from "./adyenDocumentState.helpers";
import { useAdyenDocumentState } from "./useAdyenDocumentState";

/**
 * Adyen state for one person's identity document, rendered beside the declared
 * type so seeing the wrong type and correcting it happen on one screen.
 * Memoised: the enclosing form re-renders on every keystroke.
 * Read failures are hoisted above the person list — one alert for the business.
 */
function PersonAdyenDocuments({
  businessId,
  personId,
}: {
  businessId?: string;
  personId?: string;
}) {
  const { rows, error, refetch } = useAdyenDocumentState(businessId);

  if (!businessId || !personId || error) return null;

  const groups = groupRowsBySubmission(rowsForPerson(rows, personId));
  if (groups.length === 0) return null;

  // One panel per submission, not per side: the sides are one action, and this
  // surface lists no files for the extra panels to belong to.
  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {groups.map((group) => {
        const displayed = submissionDisplayRow(group.rows);
        if (!displayed) return null;

        return (
          <AdyenDocumentPanel
            key={group.submissionKey}
            businessId={businessId}
            row={displayed}
            status="live"
            showControl={displayed === controlOwner(group.rows)}
            onSubmitted={refetch}
            otherReasons={otherSubmissionReasons(group.rows, displayed)}
          />
        );
      })}
    </Space>
  );
}

export default memo(PersonAdyenDocuments);
